import { Component, OnInit, TemplateRef, ViewChild, inject, signal, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, switchMap } from 'rxjs';

import { AppEvent, EventRound, EventPairing, EventStanding, EventRegistration, EventParticipant } from './models';
import { EventService } from './event.service';
import { emptyResultFieldValues, ResultFieldValues, seedResultFieldValues, toResultValuesPayload } from './result-fields';
import { ResultFormOutcome } from './event-result-form.component';
import { surviveRefreshError } from './refresh-stream';
import { scheduleBlockForRound } from './schedule-blocks';
import { ThemeService } from '../global';
import { Modal } from '../global/modal';
import { SimulatedTimeService } from '../global/simulated-time/simulated-time.service';
import { UserService } from '../global/userService/user-service';
import { ToastService } from '../global/toast/toast';

@Component({
    standalone: false,
    selector: 'abs-event-day',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.EVENT_DAY.TITLE' | translate">
            @if (event(); as event) {
                @if (event.requireCheckIn && myRegistration() && !myRegistration()!.checkedInAt) {
                    <ion-card color="warning">
                        <ion-card-content>
                            <p>{{ 'GLOBAL.EVENTS.EVENT_DAY.CHECK_IN_PROMPT' | translate }}</p>
                            <ion-button expand="block" (click)="checkIn(event)">
                                {{ 'GLOBAL.EVENTS.EVENT_DAY.CHECK_IN' | translate }}
                            </ion-button>
                        </ion-card-content>
                    </ion-card>
                }
                @if (nextRoundStartingSoon(); as block) {
                    <ion-card color="tertiary">
                        <ion-card-content>
                            <p>{{ 'GLOBAL.EVENTS.EVENT_DAY.NEXT_ROUND_SOON' | translate }}: {{ block.label }} &mdash; {{ block.startTime | date: 'shortTime' }}</p>
                            @if (myRegistration() && nextRoundNumberFromBlock() != null) {
                                @if (myRegistration()?.readyForRound === nextRoundNumberFromBlock()) {
                                    <p>{{ 'GLOBAL.EVENTS.EVENT_DAY.READY_CONFIRMED' | translate }}</p>
                                } @else {
                                    <ion-button expand="block" [disabled]="markingReady()" (click)="markReady()">
                                        {{ 'GLOBAL.EVENTS.EVENT_DAY.READY_BUTTON' | translate }}
                                    </ion-button>
                                }
                            }
                        </ion-card-content>
                    </ion-card>
                }
                @if (currentPairing(); as pairing) {
                    <ion-card color="primary">
                        <ion-card-content>
                            <h2>{{ 'GLOBAL.EVENTS.EVENT_DAY.YOUR_GAME' | translate }} &mdash; {{ 'GLOBAL.EVENTS.ROUNDS.ROUND' | translate }} {{ currentRoundNumber() }}</h2>
                            @if (pairing.isBye) {
                                <p>{{ 'GLOBAL.EVENTS.ROUNDS.BYE' | translate }}</p>
                            } @else {
                                <p>{{ 'GLOBAL.EVENTS.ROUNDS.TABLE' | translate }} {{ pairing.tableNumber }}</p>
                                <p>{{ 'GLOBAL.EVENTS.EVENT_DAY.OPPONENT' | translate }}: {{ opponentDisplayName(pairing) }}</p>
                            }
                            <!-- Shown on a bye too: the organiser's note for the round may say
                                 something a player sitting out still needs to know. -->
                            @if (currentRoundDescription(); as description) {
                                <abs-markdown class="event-day-round-description" [source]="description"></abs-markdown>
                            }
                        </ion-card-content>
                    </ion-card>

                    @if (!pairing.isBye) {
                        <!-- Checked before any of the result actions because the server rejects
                             reporting, confirming and disputing alike until the organiser starts
                             the round. Offering a button that cannot succeed just produces a
                             failed request and a confused player. -->
                        @if (roundNotStarted()) {
                            <ion-card color="medium">
                                <ion-card-content>
                                    {{ 'GLOBAL.EVENTS.EVENT_DAY.ROUND_NOT_STARTED' | translate: { round: currentRoundNumber() } }}
                                </ion-card-content>
                            </ion-card>
                        } @else if (!pairing.result) {
                            <!-- Refresh sits beside the result action, in every state that has
                                 one: this is where a player already is when they are waiting on
                                 their opponent, so it is where they reach to check. -->
                            <div class="event-day-actions">
                                <ion-button (click)="openReportResult()">
                                    {{ 'GLOBAL.EVENTS.EVENT_DAY.REPORT_RESULT_FOR_ROUND' | translate: { round: currentRoundNumber() } }}
                                </ion-button>
                                <abs-refresh-button [refreshing]="refreshing()" (refresh)="refresh()"></abs-refresh-button>
                            </div>
                        } @else if (!resultLocked()) {
                            <!-- Every state except the organiser's final override stays editable:
                                 confirm, dispute and report are all one tap, so any of them can be
                                 hit by accident, and none of them should be a one-way door. -->
                            <ion-card>
                                <ion-card-content>
                                    <p>{{ resultMessageKey() | translate: { name: opponentName() } }}</p>
                                    <div class="event-result-card-actions">
                                        <!-- One way in. Confirming and disagreeing both need you to
                                             have seen the result first, so they live behind this. -->
                                        <ion-button fill="outline" (click)="openReviewResult()">
                                            {{ 'GLOBAL.EVENTS.EVENT_DAY.VIEW_REPORTED_RESULT' | translate }}
                                        </ion-button>
                                        <abs-refresh-button [refreshing]="refreshing()" (refresh)="refresh()"></abs-refresh-button>
                                    </div>
                                </ion-card-content>
                            </ion-card>
                        } @else {
                            <ion-card [color]="resultColor(pairing.result.status)">
                                <ion-card-content>{{ 'GLOBAL.EVENTS.EVENT_DAY.RESULT_STATUS' | translate }}: {{ pairing.result.status }}</ion-card-content>
                            </ion-card>
                        }
                    }
                } @else {
                    <p>{{ 'GLOBAL.EVENTS.EVENT_DAY.NO_CURRENT_GAME' | translate }}</p>
                }

                @if (event.scheduleBlocks?.length) {
                    <abs-event-schedule
                        [heading]="'GLOBAL.EVENTS.EVENT_DAY.SCHEDULE' | translate"
                        [blocks]="event.scheduleBlocks"
                    ></abs-event-schedule>
                }

                @if (previousRounds().length) {
                    <ion-list>
                        <ion-list-header>{{ 'GLOBAL.EVENTS.EVENT_DAY.PREVIOUS_RESULTS' | translate }}</ion-list-header>
                        @for (entry of previousRounds(); track entry.roundNumber) {
                            <ion-item>
                                <ion-label>
                                    <h3>{{ 'GLOBAL.EVENTS.ROUNDS.ROUND' | translate }} {{ entry.roundNumber }}</h3>
                                    <p>
                                        @if (entry.isBye) {
                                            {{ 'GLOBAL.EVENTS.ROUNDS.BYE' | translate }}
                                        } @else {
                                            {{ entry.opponentName }}
                                        }
                                    </p>
                                </ion-label>
                                @if (entry.outcome) {
                                    <ion-note slot="end">
                                        {{ 'GLOBAL.EVENTS.EVENT_DAY.' + entry.outcome | translate }}
                                    </ion-note>
                                } @else {
                                    <ion-note slot="end">{{ 'GLOBAL.EVENTS.EVENT_DAY.NO_RESULT' | translate }}</ion-note>
                                }
                                <!-- Only for a closed round: until then a player seeing every
                                     table's result could learn an outcome the two players
                                     involved have not settled between themselves. -->
                                @if (entry.complete) {
                                    <ion-button slot="end" fill="clear" size="small" (click)="openRoundResults(entry)">
                                        <ion-icon name="list-outline" slot="icon-only"></ion-icon>
                                    </ion-button>
                                }
                            </ion-item>
                        }
                    </ion-list>
                }

                <div class="event-day-actions">
                    <ion-button expand="block" fill="outline" [routerLink]="['../standings']">
                        {{ 'GLOBAL.EVENTS.EVENT_DAY.VIEW_STANDINGS' | translate }}
                    </ion-button>
                </div>
            }
        </abs-page-layout>

        <ng-template #reportResultModal>
            @if (event(); as event) {
                @if (currentPairing(); as pairing) {
                    <div class="report-result-modal">
                        <p class="report-result-opponent">
                            {{ 'GLOBAL.EVENTS.EVENT_DAY.OPPONENT' | translate }}: <strong>{{ opponentName() }}</strong>
                        </p>
                        @if (event.resultScoringRules) {
                            <abs-markdown class="scoring-rules" [source]="event.resultScoringRules"></abs-markdown>
                        }
                        <abs-event-result-form
                            [resultFields]="event.resultFields ?? []"
                            [leftLabel]="'GLOBAL.EVENTS.EVENT_DAY.YOU' | translate"
                            [rightLabel]="'GLOBAL.EVENTS.EVENT_DAY.OPPONENT' | translate"
                            [leftWinLabel]="'GLOBAL.EVENTS.EVENT_DAY.YOU_WIN' | translate"
                            [drawLabel]="'GLOBAL.EVENTS.EVENT_DAY.DRAW' | translate"
                            [rightWinLabel]="'GLOBAL.EVENTS.EVENT_DAY.OPPONENT_WINS' | translate: { name: opponentName() }"
                            [leftValues]="myValues"
                            [rightValues]="opponentValues"
                            [outcome]="formOutcome"
                            (outcomeChange)="formOutcome = $event"
                            (validChange)="resultValid = $event"
                        ></abs-event-result-form>
                    </div>
                }
            }
        </ng-template>

        <ng-template #roundResultsModal>
            @if (event(); as event) {
                <ion-list class="round-results">
                    @for (pairing of roundResultsPairings(); track pairing._id) {
                        <ion-item lines="full">
                            <ion-label>
                                <h3>
                                    @if (pairing.isBye) {
                                        {{ 'GLOBAL.EVENTS.ROUNDS.BYE' | translate }} &mdash;
                                        {{ pairing.participantA.displayName || pairing.participantA.userId }}
                                    } @else {
                                        {{ 'GLOBAL.EVENTS.ROUNDS.TABLE' | translate }} {{ pairing.tableNumber }}
                                    }
                                </h3>
                                @if (!pairing.isBye) {
                                    <p>
                                        {{ pairing.participantA.displayName || pairing.participantA.userId }} vs
                                        {{ pairing.participantB?.displayName || pairing.participantB?.userId }}
                                    </p>
                                    <p class="round-results-score">
                                        @if (pairing.result) {
                                            {{ pairing.result | resultSummary: pairing : (event.resultFields ?? []) }}
                                        } @else {
                                            {{ 'GLOBAL.EVENTS.EVENT_DAY.NO_RESULT' | translate }}
                                        }
                                    </p>
                                }
                            </ion-label>
                        </ion-item>
                    }
                </ion-list>
            }
        </ng-template>

        <ng-template #reviewResultModal>
            @if (event(); as event) {
                @if (currentPairing(); as pairing) {
                    <div class="report-result-modal">
                        @if (showBothClaims() && !reviewEditing()) {
                            <!-- Both accounts, so you can see exactly where you differ rather than
                                 only the most recent report. -->
                            <p class="report-result-opponent">
                                {{ 'GLOBAL.EVENTS.EVENT_DAY.THEIR_REPORT' | translate: { name: opponentName() } }}
                            </p>
                        } @else {
                            <p class="report-result-opponent">
                                {{ 'GLOBAL.EVENTS.EVENT_DAY.REPORTED_BY' | translate: { name: opponentName() } }}
                            </p>
                        }
                        <!-- One modal, two modes. Disagreeing flips this form editable in place
                             rather than opening a second modal: ModalComponent dismisses on any
                             click reaching its host and its dismiss() does not reliably settle, so
                             chaining two modals left the new one closed the instant it appeared. -->
                        <abs-event-result-form
                            [resultFields]="event.resultFields ?? []"
                            [readOnly]="!reviewEditing()"
                            [leftLabel]="'GLOBAL.EVENTS.EVENT_DAY.YOU' | translate"
                            [rightLabel]="'GLOBAL.EVENTS.EVENT_DAY.OPPONENT' | translate"
                            [leftWinLabel]="'GLOBAL.EVENTS.EVENT_DAY.YOU_WIN' | translate"
                            [drawLabel]="'GLOBAL.EVENTS.EVENT_DAY.DRAW' | translate"
                            [rightWinLabel]="'GLOBAL.EVENTS.EVENT_DAY.OPPONENT_WINS' | translate: { name: opponentName() }"
                            [leftValues]="reviewMyValues"
                            [rightValues]="reviewOpponentValues"
                            [outcome]="reviewOutcome"
                            (outcomeChange)="reviewOutcome = $event"
                            (validChange)="resultValid = $event"
                        ></abs-event-result-form>

                        @if (showBothClaims() && !reviewEditing()) {
                            <p class="report-result-opponent report-result-mine">
                                {{ 'GLOBAL.EVENTS.EVENT_DAY.YOUR_REPORT' | translate }}
                            </p>
                            <abs-event-result-form
                                [resultFields]="event.resultFields ?? []"
                                [readOnly]="true"
                                [leftLabel]="'GLOBAL.EVENTS.EVENT_DAY.YOU' | translate"
                                [rightLabel]="'GLOBAL.EVENTS.EVENT_DAY.OPPONENT' | translate"
                                [leftWinLabel]="'GLOBAL.EVENTS.EVENT_DAY.YOU_WIN' | translate"
                                [drawLabel]="'GLOBAL.EVENTS.EVENT_DAY.DRAW' | translate"
                                [rightWinLabel]="'GLOBAL.EVENTS.EVENT_DAY.OPPONENT_WINS' | translate: { name: opponentName() }"
                                [leftValues]="myClaimMyValues"
                                [rightValues]="myClaimOpponentValues"
                                [outcome]="myClaimOutcome"
                            ></abs-event-result-form>
                        }
                    </div>
                }
            }
        </ng-template>

        <ng-template #reviewResultButtons>
            @if (event(); as event) {
                @if (currentPairing(); as pairing) {
                    <!-- Close, then dispute, then confirm: confirm is the affirmative action and
                         sits rightmost. Close is restated because buttonsTemplate replaces the
                         modal's default one. -->
                    <ion-button>{{ 'GLOBAL.CLOSE' | translate }}</ion-button>
                    <!-- Nothing to dispute a second time; leaving it would read as if the first
                         tap had not registered. Confirm stays, which is the way back. -->
                    @if (reviewEditing()) {
                        <ion-button [disabled]="!resultValid" (click)="submitReviewResult(event, pairing)">
                            {{ 'GLOBAL.EVENTS.EVENT_DAY.SUBMIT_RESULT' | translate }}
                        </ion-button>
                    } @else {
                        <!-- One button, two meanings, so its label has to say which. Editing
                             your OWN report is just a correction; editing your opponent's is
                             how you disagree with it. Either way the form becomes editable and
                             pre-filled, and it is the figures you submit -- not the button you
                             pressed -- that decide whether the result ends up disputed. -->
                        <ion-button (click)="startEditResult($event)">
                            {{ reviewEditLabelKey() | translate }}
                        </ion-button>
                        @if (canConfirm()) {
                            <ion-button (click)="confirm(event, pairing)">
                                {{ 'GLOBAL.EVENTS.EVENT_DAY.CONFIRM' | translate }}
                            </ion-button>
                        }
                    }
                }
            }
        </ng-template>

        <ng-template #reportResultButtons>
            @if (event(); as event) {
                @if (currentPairing(); as pairing) {
                    <!-- Supplying buttonsTemplate REPLACES the modal's default Close, so it has to
                         be restated here. No handler needed: a click in the button row falls
                         outside the card and ModalComponent's host listener dismisses. -->
                    <ion-button>{{ 'GLOBAL.CLOSE' | translate }}</ion-button>
                    <ion-button [disabled]="!resultValid" (click)="submitResult(event, pairing)">
                        {{ 'GLOBAL.EVENTS.EVENT_DAY.SUBMIT_RESULT' | translate }}
                    </ion-button>
                }
            }
        </ng-template>
    `,
    // Styles live in army-builder-shared/src/lib/global/styles/events.scss, per the
    // workspace rule against CSS in component files.
})
export class EventDayPage implements OnInit {
    private eventService = inject(EventService);
    private userService = inject(UserService);
    private route = inject(ActivatedRoute);
    private toastService = inject(ToastService);
    private translateService = inject(TranslateService);
    private modal = inject(Modal);
    private themeService = inject(ThemeService);
    private time = inject(SimulatedTimeService);

    @ViewChild('reportResultModal') reportResultModal: TemplateRef<any>;
    @ViewChild('reportResultButtons') reportResultButtons: TemplateRef<any>;
    @ViewChild('reviewResultModal') reviewResultModal: TemplateRef<any>;
    @ViewChild('roundResultsModal') roundResultsModal: TemplateRef<any>;

    /** Pairings shown by the round-results modal. */
    readonly roundResultsPairings = signal<EventPairing[]>([]);
    @ViewChild('reviewResultButtons') reviewResultButtons: TemplateRef<any>;

    /** The opponent's reported result, mapped from stored A/B onto my/their columns. */
    reviewMyValues: ResultFieldValues = {};
    reviewOpponentValues: ResultFieldValues = {};
    reviewOutcome: ResultFormOutcome | null = null;

    /** False while showing their report; true once you have chosen to disagree. */
    readonly reviewEditing = signal(false);

    /** This player's own claim, shown alongside the opponent's when the two disagree. */
    myClaimMyValues: ResultFieldValues = {};
    myClaimOpponentValues: ResultFieldValues = {};
    myClaimOutcome: ResultFormOutcome | null = null;
    readonly hasMyClaim = signal(false);

    /** Both accounts exist and differ -- show them side by side rather than one of them. */
    readonly showBothClaims = computed(() => this.resultStatus() === 'disputed' && this.hasMyClaim());

    event = signal<AppEvent | null>(null);
    rounds = signal<EventRound[]>([]);
    myRegistration = signal<EventRegistration | null>(null);

    /** The shared form's own LEFT/DRAW/RIGHT; mapped to WIN/DRAW/LOSS on submit. */
    formOutcome: ResultFormOutcome | null = null;
    resultValid = false;
    myValues: ResultFieldValues = {};
    opponentValues: ResultFieldValues = {};

    markingReady = signal(false);

    private refresh$ = new BehaviorSubject<void>(undefined);

    readonly currentUserId = computed(() => this.userService.user()?.userId);

    readonly currentRound = computed<EventRound | undefined>(() => {
        const rounds = this.rounds();
        return rounds.find((r) => r.status !== 'completed') ?? rounds[rounds.length - 1];
    });

    readonly currentRoundNumber = computed(() => this.currentRound()?.roundNumber);

    /**
     * What the organiser wrote about the round now being played - its scenario, any rules
     * particular to it.
     *
     * The round number comes from the EventRound document while the text lives on the schedule
     * block, and nothing in the data model joins the two, so scheduleBlockForRound bridges them
     * on the block label. A round whose block cannot be identified simply shows nothing.
     */
    readonly currentRoundDescription = computed<string | undefined>(
        () => scheduleBlockForRound(this.event()?.scheduleBlocks, this.currentRoundNumber())?.description
    );

    /** A round the organiser has generated but not yet started. Nothing can be reported on it. */
    readonly roundNotStarted = computed(() => this.currentRound()?.status === 'pending');

    readonly resultStatus = computed(() => this.currentPairing()?.result?.status);

    /**
     * Only an organiser's override is final. Everything else -- reported, confirmed,
     * disputed -- stays changeable by either player, because each of those states is
     * reached by a single tap that can be a mis-tap, and the server permits the reversal
     * in every case (confirmResult, disputeResult and reportResult all guard 'overridden'
     * and nothing else).
     */
    readonly resultLocked = computed(() => this.resultStatus() === 'overridden');

    /** True when the stored result carries this player's report or confirmation. */
    readonly respondedByMe = computed(() => {
        const pairing = this.currentPairing();
        return pairing ? this.reportedByMe(pairing) : false;
    });

    /** Whether confirming is available: the server refuses to let you confirm your own report. */
    readonly canConfirm = computed(() => !this.respondedByMe() && this.resultStatus() !== 'confirmed');

    readonly canDispute = computed(() => this.resultStatus() !== 'disputed');

    /**
     * "I Disagree" only makes sense against someone else's account of the game. Once the
     * stored report is your own, the same button is editing what YOU said, so it reads as
     * a correction instead. respondedByMe covers the disputed case too: having countered,
     * your own claim is what the form reopens.
     */
    readonly reviewEditLabelKey = computed(() =>
        this.respondedByMe() ? 'GLOBAL.EVENTS.EVENT_DAY.CHANGE_RESULT' : 'GLOBAL.EVENTS.EVENT_DAY.DISAGREE'
    );

    /** Which sentence the result card leads with, given the state and who acted. */
    readonly resultMessageKey = computed(() => {
        const mine = this.respondedByMe();
        switch (this.resultStatus()) {
            case 'confirmed':
                return 'GLOBAL.EVENTS.EVENT_DAY.RESULT_CONFIRMED';
            case 'disputed':
                return mine ? 'GLOBAL.EVENTS.EVENT_DAY.RESULT_DISPUTED_BY_OPPONENT' : 'GLOBAL.EVENTS.EVENT_DAY.RESULT_DISPUTED_BY_YOU';
            default:
                return mine ? 'GLOBAL.EVENTS.EVENT_DAY.RESULT_AWAITING_OPPONENT' : 'GLOBAL.EVENTS.EVENT_DAY.RESULT_PENDING_CONFIRMATION';
        }
    });

    readonly currentPairing = computed<EventPairing | undefined>(() => {
        const round = this.currentRound();
        const userId = this.currentUserId();
        if (!round || !userId) return undefined;
        return round.pairings.find(
            (p) => p.participantA.userId === userId || p.participantB?.userId === userId
        );
    });

    readonly opponent = computed<EventParticipant | undefined>(() => {
        const pairing = this.currentPairing();
        const userId = this.currentUserId();
        if (!pairing || !userId) return undefined;
        return pairing.participantA.userId === userId ? pairing.participantB : pairing.participantA;
    });

    readonly opponentName = computed(() => this.opponent()?.displayName || this.opponent()?.userId);

    /**
     * Rounds other than the current one that this user played, newest first. Read-only on
     * purpose: a reported result for an earlier round is not editable from here.
     */
    readonly previousRounds = computed(() => {
        const userId = this.currentUserId();
        const current = this.currentRoundNumber();
        if (!userId) return [];

        return this.rounds()
            .filter((round) => round.roundNumber !== current)
            .map((round) => {
                const pairing = round.pairings.find(
                    (p) => p.participantA.userId === userId || p.participantB?.userId === userId
                );
                if (!pairing) return null;

                const isA = pairing.participantA.userId === userId;
                const opponent = isA ? pairing.participantB : pairing.participantA;
                const outcome = pairing.result
                    ? pairing.result.outcome === 'DRAW'
                        ? 'DRAW'
                        : (pairing.result.outcome === 'A_WIN') === isA
                          ? 'WIN'
                          : 'LOSS'
                    : null;

                return {
                    roundNumber: round.roundNumber,
                    isBye: pairing.isBye,
                    opponentName: opponent?.displayName || opponent?.userId,
                    outcome,
                    status: pairing.result?.status ?? null,
                    // Everyone's results, but only once the organiser has closed the round --
                    // see roundResultsVisible.
                    complete: round.status === 'completed',
                    pairings: round.pairings
                };
            })
            .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
            .sort((a, b) => b.roundNumber - a.roundNumber);
    });

    readonly nextRoundStartingSoon = computed(() => {
        const event = this.event();
        if (!event?.scheduleBlocks?.length) return undefined;
        // Via SimulatedTimeService, not Date.now(), so an admin winding the clock forward
        // sees this prompt exactly as a player will on the day -- not just the schedule line.
        const now = this.time.nowMs();
        const TEN_MIN_MS = 10 * 60 * 1000;
        return event.scheduleBlocks.find(
            (b) => b.type === 'round' && new Date(b.startTime).getTime() - now > 0 && new Date(b.startTime).getTime() - now <= TEN_MIN_MS
        );
    });

    readonly nextRoundNumberFromBlock = computed<number | undefined>(() => {
        const block = this.nextRoundStartingSoon();
        if (!block) return undefined;
        // Mirrors round-reminder.service.ts's round-number extraction (schedule block label -> digits)
        // so the "ready" UI and the backend reminder job agree on which round is meant. Keep this in
        // sync with that file if its extraction logic ever changes.
        const match = block.label.match(/(\d+)/);
        return match ? Number(match[1]) : undefined;
    });

    ngOnInit() {
        combineLatest([this.route.paramMap, this.refresh$])
            .pipe(
                switchMap(([params]) =>
                    this.eventService.getEvent(params.get('eventId')!).pipe(surviveRefreshError(() => this.refreshFailed()))
                )
            )
            .subscribe((event) => {
                this.event.set(event);
                this.myValues = emptyResultFieldValues(event.resultFields ?? []);
                this.opponentValues = emptyResultFieldValues(event.resultFields ?? []);
            });

        combineLatest([this.route.paramMap, this.refresh$])
            .pipe(
                switchMap(([params]) =>
                    this.eventService.getRounds(params.get('eventId')!).pipe(surviveRefreshError(() => this.refreshFailed()))
                )
            )
            .subscribe((rounds) => {
                this.rounds.set(rounds);
                // Cleared on the rounds fetch: it carries the opponent's report, which is the
                // thing a player presses refresh to look for.
                this.refreshing.set(false);
            });

        combineLatest([this.route.paramMap, this.refresh$])
            .pipe(
                switchMap(([params]) =>
                    this.eventService.getMyRegistration(params.get('eventId')!).pipe(surviveRefreshError(() => this.refreshFailed()))
                )
            )
            .subscribe((registration) => this.myRegistration.set(registration));
    }

    /** True while a manual refresh is in flight, so the button cannot be queued up twice. */
    readonly refreshing = signal(false);

    /** Clears the in-flight flag and says so, without ending the stream. See surviveRefreshError. */
    private refreshFailed() {
        this.refreshing.set(false);
        this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.REFRESH_FAILED'), {
            color: 'danger',
            duration: 4000
        });
    }

    /** Manual reload, for seeing an opponent's report land without leaving the page. */
    refresh() {
        this.refreshing.set(true);
        this.refresh$.next();
    }

    opponentDisplayName(pairing: EventPairing): string | undefined {
        const userId = this.currentUserId();
        const opponent = pairing.participantA.userId === userId ? pairing.participantB : pairing.participantA;
        return opponent?.displayName || opponent?.userId;
    }

    /**
     * Shows what the opponent actually reported, from this player's point of view --
     * the stored result is always in participantA/participantB terms, which is only
     * "me/them" if I happen to be participant A.
     */
    openReviewResult() {
        const event = this.event();
        const pairing = this.currentPairing();
        const result = pairing?.result;
        if (!event || !pairing || !result) return;

        const isA = pairing.participantA.userId === this.currentUserId();
        const resultFields = event.resultFields ?? [];

        // Read the OPPONENT'S OWN claim, not the merged top-level figures. Those hold
        // whichever report landed last -- so after disagreeing you were shown your own
        // numbers back under their name.
        const theirClaim = isA ? result.participantBClaim : result.participantAClaim;
        const myClaim = isA ? result.participantAClaim : result.participantBClaim;
        const theirs = theirClaim ?? result;

        this.reviewMyValues = seedResultFieldValues(resultFields, isA ? theirs.participantAValues : theirs.participantBValues);
        this.reviewOpponentValues = seedResultFieldValues(resultFields, isA ? theirs.participantBValues : theirs.participantAValues);
        this.reviewOutcome = theirs.outcome === 'DRAW' ? 'DRAW' : (theirs.outcome === 'A_WIN') === isA ? 'LEFT' : 'RIGHT';

        // Kept separately so a dispute can show both accounts side by side.
        if (myClaim) {
            this.myClaimMyValues = seedResultFieldValues(resultFields, isA ? myClaim.participantAValues : myClaim.participantBValues);
            this.myClaimOpponentValues = seedResultFieldValues(resultFields, isA ? myClaim.participantBValues : myClaim.participantAValues);
            this.myClaimOutcome = myClaim.outcome === 'DRAW' ? 'DRAW' : (myClaim.outcome === 'A_WIN') === isA ? 'LEFT' : 'RIGHT';
        }
        this.hasMyClaim.set(!!myClaim);
        this.reviewEditing.set(false);

        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.EVENTS.EVENT_DAY.REVIEW_RESULT_TITLE', {
                    round: this.currentRoundNumber()
                }),
                contentTemplate: this.reviewResultModal,
                buttonsTemplate: this.reviewResultButtons,
                buttonsDirection: 'horizontal',
                cssClass: `${this.themeService.theme()} no-background`
            }
        });
    }

    /**
     * Turns the open review modal into an editable form, pre-filled so you only change
     * what is actually wrong -- whether that is correcting your own report or countering
     * your opponent's. See reviewEditLabelKey for which of the two the button offers.
     *
     * stopPropagation because ModalComponent dismisses on any click that reaches its
     * host, and this click must not close the modal it is switching the mode of.
     */
    startEditResult(clickEvent: Event) {
        clickEvent.stopPropagation();
        // Start from your own last account if you have one, so re-editing a dispute does
        // not throw away what you previously said; otherwise start from theirs.
        if (this.hasMyClaim()) {
            this.reviewMyValues = { ...this.myClaimMyValues };
            this.reviewOpponentValues = { ...this.myClaimOpponentValues };
            this.reviewOutcome = this.myClaimOutcome;
        }
        this.resultValid = false;
        this.reviewEditing.set(true);
    }

    /** Submits the edited review form as this player's own report. */
    submitReviewResult(event: AppEvent, pairing: EventPairing) {
        this.myValues = this.reviewMyValues;
        this.opponentValues = this.reviewOpponentValues;
        this.formOutcome = this.reviewOutcome;
        this.submitResult(event, pairing);
    }

    /** Every table's result for a round the organiser has closed. */
    openRoundResults(entry: { roundNumber: number; pairings: EventPairing[] }) {
        this.roundResultsPairings.set(entry.pairings);
        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.EVENTS.EVENT_DAY.ROUND_RESULTS_TITLE', { round: entry.roundNumber }),
                contentTemplate: this.roundResultsModal,
                cssClass: `${this.themeService.theme()} no-background`
            }
        });
    }

    openReportResult() {
        // Pre-filled when a result already exists, so this doubles as "change result" --
        // correcting your own report, or countering one you disagree with. Submitting
        // overwrites the stored result and returns it to 'pending' (see reportResult).
        const event = this.event();
        const pairing = this.currentPairing();
        const result = pairing?.result;
        const resultFields = event?.resultFields ?? [];
        if (result && pairing) {
            const isA = pairing.participantA.userId === this.currentUserId();
            this.myValues = seedResultFieldValues(resultFields, isA ? result.participantAValues : result.participantBValues);
            this.opponentValues = seedResultFieldValues(resultFields, isA ? result.participantBValues : result.participantAValues);
            this.formOutcome = result.outcome === 'DRAW' ? 'DRAW' : (result.outcome === 'A_WIN') === isA ? 'LEFT' : 'RIGHT';
        } else {
            this.myValues = emptyResultFieldValues(resultFields);
            this.opponentValues = emptyResultFieldValues(resultFields);
            this.formOutcome = null;
        }
        this.resultValid = false;

        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.EVENTS.EVENT_DAY.REPORT_RESULT_FOR_ROUND', {
                    round: this.currentRoundNumber()
                }),
                contentTemplate: this.reportResultModal,
                buttonsTemplate: this.reportResultButtons,
                buttonsDirection: 'horizontal',
                // Themes the modal to the game whose route this page sits under, per the
                // repo's modal guidance. ThemeService rather than a hardcoded class because
                // this page is shared across every game.
                cssClass: `${this.themeService.theme()} no-background`
            }
        });
    }

    reportedByMe(pairing: EventPairing): boolean {
        const userId = this.currentUserId();
        const isA = pairing.participantA.userId === userId;
        return isA ? !!pairing.result?.participantAReported : !!pairing.result?.participantBReported;
    }

    submitResult(event: AppEvent, pairing: EventPairing) {
        const round = this.currentRound()!;
        const resultFields = event.resultFields ?? [];
        this.eventService
            .reportResult(event._id!, round._id!, pairing._id!, {
                // LEFT is always this player's own column -- see the form's leftLabel above.
                outcome: this.formOutcome === 'DRAW' ? 'DRAW' : this.formOutcome === 'LEFT' ? 'WIN' : 'LOSS',
                myValues: toResultValuesPayload(resultFields, this.myValues),
                opponentValues: toResultValuesPayload(resultFields, this.opponentValues)
            })
            .subscribe({
                next: () => this.refresh$.next(),
                error: (err) => {
                    // Surfaces the server's own reason when it sent one (e.g. the organiser
                    // edited resultFields while this page had a stale field set loaded), rather
                    // than a fixed message that would hide genuinely different failures behind
                    // identical text.
                    const message = err?.error?.message || this.translateService.instant('GLOBAL.EVENTS.EVENT_DAY.SUBMIT_RESULT_FAILED');
                    this.toastService.showToast('', message, { color: 'danger', duration: 4000 });
                }
            });
    }

    confirm(event: AppEvent, pairing: EventPairing) {
        const round = this.currentRound()!;
        this.eventService.confirmResult(event._id!, round._id!, pairing._id!).subscribe(() => this.refresh$.next());
    }

    dispute(event: AppEvent, pairing: EventPairing) {
        const round = this.currentRound()!;
        this.eventService.disputeResult(event._id!, round._id!, pairing._id!).subscribe(() => this.refresh$.next());
    }

    checkIn(event: AppEvent) {
        const reg = this.myRegistration();
        if (!reg) return;
        this.eventService.checkIn(event._id!, reg._id!).subscribe(() => this.refresh$.next());
    }

    markReady() {
        const event = this.event();
        const reg = this.myRegistration();
        const round = this.nextRoundNumberFromBlock();
        if (!event || !reg || round == null) return;

        this.markingReady.set(true);
        this.eventService.markReadyForRound(event._id!, reg._id!, round).subscribe({
            next: () => {
                this.markingReady.set(false);
                this.refresh$.next();
            },
            error: () => this.markingReady.set(false),
        });
    }

    resultColor(status: string): string {
        switch (status) {
            case 'confirmed':
            case 'overridden':
                return 'success';
            case 'disputed':
                return 'danger';
            default:
                return 'medium';
        }
    }
}
